import React, { useCallback } from 'react';
import DayPicker from 'react-day-picker';
import styled from 'styled-components';
import { themeGet } from '@styled-system/theme-get';
import { Button } from '../button/Button';
import { Select } from '../select/Select';
import { Icon } from '../icon/Icon';
import * as DateUtils from './dateUtils';
import * as DateFns from 'date-fns/fp';

const Navbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: absolute;
  top: 0;
  right: 0;
  left: 0;
`;

const StyledNavBarButton = styled(Button)`
  height: auto;
  padding: 8px;
`;

const StyledSelect = styled(Select)`
  background-color: ${themeGet('colors.transparent')};
  border: 0;

  &:hover,
  &:focus {
    border: 0;
  }
`;

const CaptionContainer = styled.div`
  position: relative;
  display: inline-flex;
  flex-direction: row;
  justify-content: space-around;
  align-items: center;
  margin: 0 28px;
  min-height: 36px;
`;

const StyledDayPicker = styled(DayPicker)`
  box-sizing: border-box;
  font-family: OpenSans;
  font-size: 13px;
  line-height: 1.5;
  position: relative;
  min-width: 156px;
  vertical-align: top;

  * {
    outline: 0;
  }

  .DayPicker-Caption {
    display: table-caption;
    margin: 0 -8px;
  }

  .DayPicker-Months {
    display: flex;
    justify-content: space-around;
  }

  .DayPicker-Month {
    display: inline-table;
    margin: 0 8px 8px;
    border-collapse: collapse;
    border-spacing: 0;
    user-select: none;
  }

  .DayPicker-Weekdays {
    display: table-header-group;
  }

  .DayPicker-WeekdaysRow {
    display: table-row;
  }

  .DayPicker-Weekday {
    display: table-cell;
    width: 30px;
    height: 30px;
    vertical-align: middle;
    text-align: center;
    font-weight: 600;
    padding-top: 8px;

    abbr[title] {
      text-decoration: none;
    }
  }

  .DayPicker-Body {
    display: table-row-group;
  }

  .DayPicker-Week {
    display: table-row;
  }

  .DayPicker-Day,
  .DayPicker-WeekNumber {
    display: table-cell;
    width: 30px;
    height: 30px;
    vertical-align: middle;
    text-align: center;
    line-height: 1.5;
  }

  .DayPicker-Day {
    border-radius: 5px;
    cursor: pointer;
    color: ${themeGet('calendar.day.color')};

    &:hover,
    &:focus {
      color: ${themeGet('calendar.day.hover.color')};
      background-color: ${themeGet('calendar.day.hover.backgroundColor')};
    }

    &:active {
      color: ${themeGet('calendar.day.active.color')};
      background-color: ${themeGet('calendar.day.active.backgroundColor')};
    }

    &.DayPicker-Day--disabled,
    &.DayPicker-Day--outside {
      color: ${themeGet('calendar.day.disabled.color')} !important;
      background-color: ${themeGet('calendar.day.disabled.backgroundColor')} !important;
    }

    &.DayPicker-Day--selected {
      border-radius: 5px;
      color: ${themeGet('calendar.day.selected.color')};
      background-color: ${themeGet('calendar.day.selected.backgroundColor')};
      &:hover {
        background-color: ${themeGet('calendar.day.selected.hover.backgroundColor')};
      }
    }

    &.DayPicker-Day--selected-range,
    &.DayPicker-Day--hovered-range {
      border-radius: 0;
      color: ${themeGet('calendar.day.selected.range.color')};
      background-color: ${themeGet('calendar.day.selected.range.backgroundColor')};
    }
  }
`;

const Divider = styled.div`
  border-bottom: ${themeGet('calendar.separator.border')};
`;

const DatePickerCaption = ({
  classNames,
  date,
  onChange,
  locale,
  localeUtils,
  minDate,
  maxDate,
  months = localeUtils.getMonths(locale),
}) => {
  const minYear = minDate.getFullYear();
  const maxYear = maxDate.getFullYear();
  const displayMonth = date.getMonth();
  const displayYear = date.getFullYear();

  // build the list of available months, limiting based on minDate and maxDate as necessary
  const startMonth = displayYear === minYear ? minDate.getMonth() : 0;
  const endMonth = displayYear === maxYear ? maxDate.getMonth() + 1 : undefined;
  const monthOptions = months.map((month, i) => ({ name: month, value: i })).slice(startMonth, endMonth);
  const yearOptions = DateUtils.getYearsBetween([minYear, maxYear]).map(year => ({
    name: year,
    value: year,
  }));

  const handleMonthChange = useCallback(
    ({ value }) => {
      const newDate = DateUtils.clone(date);
      newDate.setMonth(value);

      onChange &&
        onChange({
          target: {
            value: newDate,
          },
        });
    },
    [date, onChange]
  );

  const handleYearChange = useCallback(
    ({ value }) => {
      const newDate = DateUtils.clone(date);
      newDate.setFullYear(value);

      onChange &&
        onChange({
          target: {
            value: newDate,
          },
        });
    },
    [date, onChange]
  );

  return (
    <div className={classNames.caption}>
      <CaptionContainer>
        <StyledSelect
          width="124px"
          onChange={handleYearChange}
          value={yearOptions.find(({ value }) => value === displayYear)}
          options={yearOptions}
          placeholder="Year"
        />
        <StyledSelect
          onChange={handleMonthChange}
          value={monthOptions.find(({ value }) => value === displayMonth)}
          options={monthOptions}
          placeholder="Month"
        />
      </CaptionContainer>
      <Divider />
    </div>
  );
};

const DatePickerNavbar = ({ classNames, month, maxDate, minDate, onNextClick, onPreviousClick }) => {
  return (
    <Navbar className={classNames.navBar}>
      <StyledNavBarButton
        type="button"
        variant="link"
        size="small"
        disabled={DateFns.isSameMonth(month, minDate)}
        onClick={useCallback(() => onPreviousClick(), [onPreviousClick])}
      >
        <Icon name="ARROW_LEFT" fontSize={20} color="blacks.7" />
      </StyledNavBarButton>
      <StyledNavBarButton
        type="button"
        variant="link"
        size="small"
        disabled={DateFns.isSameMonth(month, maxDate)}
        onClick={useCallback(() => onNextClick(), [onNextClick])}
      >
        <Icon name="ARROW_RIGHT" fontSize={20} color="blacks.7" />
      </StyledNavBarButton>
    </Navbar>
  );
};

const EnhancedDayPicker = ({ onChange, ...restProps }) => {
  const { fromMonth, toMonth } = restProps;
  const renderCaption = useCallback(
    props => <DatePickerCaption {...props} onChange={onChange} maxDate={toMonth} minDate={fromMonth} />,
    [onChange, fromMonth, toMonth]
  );

  const renderNavbar = useCallback(
    props => <DatePickerNavbar {...props} maxDate={toMonth} minDate={fromMonth} />,
    [toMonth, fromMonth]
  );

  return <StyledDayPicker captionElement={renderCaption} navbarElement={renderNavbar} {...restProps} />;
};

export default EnhancedDayPicker;
